/**
 * Moteur calendrier PUR — partagé entre SaaS Nuxt et widget standalone.
 *
 * Logique de grille mensuelle (Monday-first), dédup des départs, collecte des
 * dates disponibles et règle de sélectionnabilité. Aucune dépendance
 * Vue/Pinia/Nuxt : chaque côté appelle ces fonctions avec SA propre source de
 * données (le store n'est jamais touché ici).
 */

import type { Journey } from '../types'
import type { Direction } from '../types/enums'

// ── Primitives de grille ────────────────────────────────────────────────

/**
 * Offset (nombre de cellules vides) avant le 1ᵉʳ jour du mois, en semaine
 * Monday-first. Verbatim SaaS/widget : `(getDay() + 6) % 7` (Lun=0..Dim=6).
 */
export function monthFirstDayOffset(year: number, month: number): number {
  const firstDay = new Date(year, month, 1).getDay() // 0=Dim..6=Sam
  return (firstDay + 6) % 7
}

/** Nombre de jours dans le mois (`month` 0-based). */
export function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate()
}

/**
 * Grille mensuelle Monday-first : `offset` × `null` (cellules vides du début de
 * semaine) suivis des numéros de jour `1..N`. Source unique pour le rendu en
 * grille (widget `cells`) et dérivable côté SaaS (`firstDayOfMonth`/`daysInMonth`).
 */
export function buildMonthCells(year: number, month: number): (number | null)[] {
  const offset = monthFirstDayOffset(year, month)
  const days = daysInMonth(year, month)
  const cells: (number | null)[] = Array.from({ length: offset }, () => null)
  for (let d = 1; d <= days; d++) cells.push(d)
  return cells
}

// ── Dédup des départs ───────────────────────────────────────────────────

/** Départ minimal pour la dédup d'affichage : compagnie + heure. */
export interface DepartureLike {
  company?: string | null
  time?: string | null
}

/**
 * Déduplique une liste de départs par clé `company|time` — un même ferry
 * partagé entre 2 journey_id (cas backend connu) n'apparaît qu'une fois.
 * Préserve l'ordre d'apparition.
 *
 * `opts.lowercaseCompany` reproduit la clé SaaS (`getDeparturesForDay` →
 * `company.toLowerCase()|time`) ; par défaut la clé widget (casse préservée).
 */
export function dedupDepartures<T extends DepartureLike>(
  departures: readonly T[],
  opts?: { lowercaseCompany?: boolean },
): T[] {
  const lower = opts?.lowercaseCompany ?? false
  const seen = new Set<string>()
  const out: T[] = []
  for (const dep of departures ?? []) {
    const company = String(dep?.company ?? '')
    const time = String(dep?.time ?? '')
    const key = `${lower ? company.toLowerCase() : company}|${time}`
    if (seen.has(key)) continue
    seen.add(key)
    out.push(dep)
  }
  return out
}

// ── Dates disponibles + sélectionnabilité ───────────────────────────────

/** Conversion Date → ISO `YYYY-MM-DD` (timezone locale, sans heure). */
export function toIsoDate(d: Date): string {
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

/**
 * Ensemble des dates ISO ayant au moins un départ pour la `direction` donnée,
 * dérivé d'une liste explicite de `journeys` (entrée fournie par l'appelant —
 * le widget passe `store.search.journeys`). Pure : ne lit aucun store.
 */
export function collectAvailableDates(
  journeys: readonly Journey[],
  direction: Direction,
): Set<string> {
  const set = new Set<string>()
  for (const journey of journeys ?? []) {
    if (journey.direction !== direction) continue
    for (const dep of journey.departure ?? []) {
      // `dep.depart` est déjà une date ISO `YYYY-MM-DD` (cf. backend `to_char`).
      // On l'indexe directement : le round-trip `toIsoDate(new Date(...))`
      // était inutile et décalait la date d'un jour en fuseau négatif (UTC-).
      if (dep?.depart) set.add(dep.depart.slice(0, 10))
    }
  }
  return set
}

/**
 * Règle de sélectionnabilité d'une date ISO :
 *   - la date doit avoir un départ (`availableDates.has(iso)`),
 *   - en mode `out` : `iso >= todayIso`,
 *   - en mode `ret` : un aller doit être posé ET `iso >= outDate`.
 *
 * `todayIso` est injectable pour rester pure (par défaut : aujourd'hui local).
 */
export function isDateSelectable(
  iso: string,
  params: {
    mode: 'out' | 'ret'
    outDate: string | null
    availableDates: Set<string>
    todayIso?: string
  },
): boolean {
  const { mode, outDate, availableDates } = params
  if (!availableDates.has(iso)) return false
  if (mode === 'out') {
    const today = params.todayIso ?? toIsoDate(new Date())
    return iso >= today
  }
  return !!outDate && iso >= outDate
}
